/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { vehicles } from "../../data/vehicles.ts";
import "../../styles/VehicleScreensaver.css";

const VehicleScreensaver = ({ onExit }) => {
  const [currentVehicle, setCurrentVehicle] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  /* ---------- refs for timers & “session id” ---------- */
  const typingTimeoutRef = useRef(null);
  const autoAdvanceTimeoutRef = useRef(null);
  const sessionRef = useRef(0); // ← NEW: identifies the active round

  const clearAllTimeouts = () => {
    clearTimeout(typingTimeoutRef.current);
    clearTimeout(autoAdvanceTimeoutRef.current);
    typingTimeoutRef.current = null;
    autoAdvanceTimeoutRef.current = null;
  };

  /* ---------- pick a vehicle + image ---------- */
  const loadNewVehicle = useCallback(() => {
    clearAllTimeouts();
    sessionRef.current += 1; // bump session → invalidates old timers

    const vehicle = vehicles[Math.floor(Math.random() * vehicles.length)];
    const imgIdx = Math.floor(Math.random() * vehicle.images.length);

    setCurrentVehicle(vehicle); // triggers typing via useEffect
    setCurrentImageIndex(imgIdx);
  }, []);

  /* ---------- typewriter effect (fires when vehicle changes) ---------- */
  useEffect(() => {
    if (!currentVehicle) return;

    setDisplayedText("");
    setIsTyping(true);

    let charIndex = 0;
    const mySession = sessionRef.current; // capture session for this run

    const typeChar = () => {
      // bail out if a newer session has started
      if (mySession !== sessionRef.current) return;

      if (charIndex <= currentVehicle.description.length) {
        setDisplayedText(currentVehicle.description.slice(0, charIndex));
        charIndex += 1;
        typingTimeoutRef.current = setTimeout(typeChar, 50);
      } else {
        setIsTyping(false);
        typingTimeoutRef.current = null;
        autoAdvanceTimeoutRef.current = setTimeout(loadNewVehicle, 3000);
      }
    };

    typeChar();
    return clearAllTimeouts; // clean up when component unmounts
  }, [currentVehicle, loadNewVehicle]);

  /* ---------- auto-scroll while typing ---------- */
  const textContainerRef = useRef(null);
  useEffect(() => {
    textContainerRef.current &&
      (textContainerRef.current.scrollTop =
        textContainerRef.current.scrollHeight);
  }, [displayedText]);

  /* ---------- start on mount ---------- */
  useEffect(() => {
    loadNewVehicle();
    return clearAllTimeouts;
  }, [loadNewVehicle]);

  /* ---------- exit on user activity ---------- */
  useEffect(() => {
    const handleUserActivity = (e) => {
      e.preventDefault();
      onExit();
    };
    const events = ["click", "touchstart", "keydown", "mousemove", "scroll"];
    events.forEach((ev) =>
      document.addEventListener(ev, handleUserActivity, { passive: false })
    );
    return () =>
      events.forEach((ev) =>
        document.removeEventListener(ev, handleUserActivity)
      );
  }, [onExit]);

  if (!currentVehicle)
    return <div className="vehicle-screensaver">Loading…</div>;

  /* ---------- render ---------- */
  return (
    <div className="vehicle-screensaver">
      <div className="screensaver-content">
        <div className="vehicle-image-container">
          <img
            src={currentVehicle.images[currentImageIndex]}
            alt={currentVehicle.title}
            className="vehicle-fullscreen-image"
            style={{
              width: "100%",
              height: "70vh",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
          <div className="vehicle-title-overlay">
            <h1>{currentVehicle.title}</h1>
          </div>
        </div>

        <div className="vehicle-description-container">
          <div
            ref={textContainerRef}
            className="typewriter-text"
            style={{
              height: "200px",
              overflowY: "auto",
              padding: "20px",
              backgroundColor: "rgba(0,0,0,0.7)",
              borderRadius: "8px",
              margin: "20px",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <style>{`.typewriter-text::-webkit-scrollbar{display:none;}`}</style>
            {displayedText}
            {isTyping && <span className="cursor">|</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleScreensaver;
